import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { Sky } from 'three/addons/objects/Sky.js';
import { requireElement } from '../dom';

export interface SceneContext {
  container: HTMLElement;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer | null;
  controls: OrbitControls | null;
}

/** Matches DirectionalLight position — shared sun direction for sky + shadows. */
export const SUN_POSITION = new THREE.Vector3(20, 40, 15);

export function createSceneContext(): SceneContext {
  const container = requireElement('canvas-container');
  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(0xa8c8e8, 0.009);

  const sky = new Sky();
  sky.scale.setScalar(4500);
  const skyUniforms = sky.material.uniforms;
  skyUniforms['turbidity'].value = 4;
  skyUniforms['rayleigh'].value = 1.8;
  skyUniforms['mieCoefficient'].value = 0.005;
  skyUniforms['mieDirectionalG'].value = 0.75;
  skyUniforms['sunPosition'].value.copy(SUN_POSITION);
  scene.add(sky);

  const camera = new THREE.PerspectiveCamera(40, 1, 1, 1000);
  camera.position.set(20, 18, 24);

  let renderer: THREE.WebGLRenderer | null = null;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(renderer.domElement);
  } catch (e) {
    console.error('WebGL hiba:', e);
    document.getElementById('webgl-error')?.classList.remove('hidden');
  }

  const controls = renderer ? new OrbitControls(camera, renderer.domElement) : null;
  if (controls) {
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.maxPolarAngle = Math.PI / 2.05;
  }

  return { container, scene, camera, renderer, controls };
}

export function addLights(scene: THREE.Scene): void {
  const ambientLight = new THREE.AmbientLight(0xd6e4f5, 1.35);
  scene.add(ambientLight);

  const sunLight = new THREE.DirectionalLight(0xfff6e8, 2.4);
  sunLight.position.copy(SUN_POSITION);
  sunLight.castShadow = true;
  sunLight.shadow.mapSize.width = 1024;
  sunLight.shadow.mapSize.height = 1024;
  sunLight.shadow.camera.near = 0.5;
  sunLight.shadow.camera.far = 100;
  const d = 20;
  sunLight.shadow.camera.left = -d;
  sunLight.shadow.camera.right = d;
  sunLight.shadow.camera.top = d;
  sunLight.shadow.camera.bottom = -d;
  sunLight.shadow.bias = -0.0003;
  scene.add(sunLight);

  const hemiLight = new THREE.HemisphereLight(0xb8d4f0, 0x6b8f5a, 0.95);
  scene.add(hemiLight);
}

export function observeResize(ctx: SceneContext): void {
  const { container, camera, renderer } = ctx;
  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const width = entry.contentRect.width || container.clientWidth;
      const height = entry.contentRect.height || container.clientHeight;
      if (width > 0 && height > 0 && renderer) {
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
      }
    }
  });
  resizeObserver.observe(container);
}
